import { FirestoreService } from './firestore';
import type {
  CommunityForum,
  ForumThread,
  ThreadReply,
  CommunityConnection,
  LawyerProfile,
  CommunityNotification,
  CreateForumData,
  CreateThreadData,
  UpdateLawyerProfileData,
  CommunityStats,
  ForumCategory,
  ConnectionStatus
} from '../types/community';

// Collection References
const FORUMS_REF = 'CommunityForums';
const FORUM_THREADS_REF = 'CommunityThreads';
const THREAD_REPLIES_REF = 'CommunityThreadReplies';
const CONNECTIONS_REF = 'CommunityConnections';
const LAWYER_PROFILES_REF = 'CommunityLawyerProfiles';
const NOTIFICATIONS_REF = 'CommunityNotifications';
const FORUM_CATEGORIES_REF = 'CommunityForumCategories';

export class CommunityService {
  // Forum Operations
  static async getForums(): Promise<CommunityForum[]> {
    console.log('🆕 NEW CODE VERSION - CommunityService.getForums() called');
    console.log('🔍 CommunityService - Fetching forums from Firestore...');
    
    // First, get all forums without any ordering to avoid index issues
    const allForums = await FirestoreService.getDocuments<CommunityForum>(FORUMS_REF, []);
    console.log('🔍 CommunityService - All forums from Firestore:', allForums.length, 'forums');
    
    // Filter and sort in memory
    const filteredForums = allForums.filter(forum => 
      forum.isDeleted === false && forum.isActive === true
    );
    
    console.log('🔍 CommunityService - Filtered forums:', filteredForums.length, 'forums');
    console.log('🔍 CommunityService - Forum details:', filteredForums.map(f => ({
      id: f.id,
      title: f.title,
      isActive: f.isActive,
      isDeleted: f.isDeleted,
      categoryId: f.categoryId,
      createdAt: f.createdAt
    })));
    
    // Sort in memory for better control
    const sortedForums = filteredForums.sort((a, b) => {
      // First sort by isPinned (pinned forums first)
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      
      // Then sort by lastThreadAt (most recent activity first)
      if (a.lastThreadAt && b.lastThreadAt) {
        return parseInt(b.lastThreadAt) - parseInt(a.lastThreadAt);
      }
      if (a.lastThreadAt && !b.lastThreadAt) return -1;
      if (!a.lastThreadAt && b.lastThreadAt) return 1;
      
      // Finally sort by createdAt (newest first)
      return parseInt(b.createdAt) - parseInt(a.createdAt);
    });
    
    console.log('🔍 CommunityService - Sorted forums:', sortedForums.length, 'forums');
    return sortedForums;
  }

  static async getForumById(forumId: string): Promise<CommunityForum | null> {
    return FirestoreService.getDocument<CommunityForum>(FORUMS_REF, forumId);
  }

  static async getForumThreads(forumId: string, limit = 20): Promise<ForumThread[]> {
    return FirestoreService.getDocuments<ForumThread>(FORUM_THREADS_REF, [
      FirestoreService.where('forumId', '==', forumId),
      FirestoreService.where('isDeleted', '==', false),
      FirestoreService.orderBy('isPinned', 'desc'),
      FirestoreService.orderBy('createdAt', 'desc'),
      FirestoreService.limit(limit)
    ]);
  }

  static async getThreadById(threadId: string): Promise<ForumThread | null> {
    return FirestoreService.getDocument<ForumThread>(FORUM_THREADS_REF, threadId);
  }

  static async getThreadReplies(threadId: string): Promise<ThreadReply[]> {
    return FirestoreService.getDocuments<ThreadReply>(THREAD_REPLIES_REF, [
      FirestoreService.where('threadId', '==', threadId),
      FirestoreService.where('isDeleted', '==', false),
      FirestoreService.orderBy('createdAt', 'asc')
    ]);
  }

  // Forum Replies (as subcollection)
  static async getForumReplies(forumId: string): Promise<ThreadReply[]> {
    const repliesRef = `CommunityForums/${forumId}/Replies`;
    console.log('🔄 Getting forum replies from:', repliesRef);
    
    // Get all replies without filters to avoid index issues, then filter client-side
    const allReplies = await FirestoreService.getDocuments<ThreadReply>(repliesRef);
    console.log('📝 Raw replies from Firestore:', allReplies);
    
    // Filter and sort client-side
    const filteredReplies = allReplies
      .filter(reply => reply.isDeleted === false)
      .sort((a, b) => parseInt(a.createdAt) - parseInt(b.createdAt));
    
    console.log('📝 Filtered and sorted replies:', filteredReplies);
    return filteredReplies;
  }

  static async addForumReply(forumId: string, content: string, userId: string, parentReplyId?: string): Promise<string> {
    const repliesRef = `CommunityForums/${forumId}/Replies`;
    
    // Get user data from Firestore
    let userData = { name: '', email: '', photoURL: '', title: '' };
    try {
      const userDoc = await FirestoreService.getDocument('Users', userId);
      if (userDoc) {
        console.log('📝 User document data:', userDoc);
        
        // Try different name fields that might exist
        const userName = userDoc.name || 
                        userDoc.displayName || 
                        userDoc.fullName || 
                        userDoc.firstName + ' ' + userDoc.lastName || 
                        userDoc.email?.split('@')[0] || 
                        'Anonymous';
        
        userData = {
          name: userName,
          email: userDoc.email || '',
          photoURL: userDoc.photoURL || userDoc.avatar || '',
          title: userDoc.title || userDoc.jobTitle || userDoc.position || ''
        };
        
        console.log('📝 Extracted user data:', userData);
      }
    } catch (error) {
      console.warn('Could not fetch user data, using defaults:', error);
    }
    
    // Create complete reply data with all fields and default values
    // This ensures consistent structure in Firestore with no undefined fields
    const replyData: any = {
      threadId: forumId, // Using forumId as threadId for forum-level replies
      content,
      authorId: userId,
      authorName: userData.name,
      authorPhotoURL: userData.photoURL || '', // Always include with default
      authorTitle: userData.title || '', // Always include with default
      parentReplyId: parentReplyId || '', // Always include with default
      likeCount: 0,
      likedBy: [], // Always initialize empty array for likes
      isEdited: false,
      createdAt: Date.now().toString(),
      updatedAt: Date.now().toString(),
      createdBy: userId,
      updatedBy: userId,
      isDeleted: false,
      isArchived: false
    };

    console.log('📝 Adding forum reply:', {
      forumId,
      content: content.substring(0, 50) + '...',
      authorName: userData.name,
      authorEmail: userData.email,
      authorId: userId
    });

    console.log('📝 Reply data being sent to Firestore:', replyData);
    console.log('📝 Reply data keys:', Object.keys(replyData));
    console.log('📝 parentReplyId value:', replyData.parentReplyId);
    console.log('📝 parentReplyId type:', typeof replyData.parentReplyId);

    return FirestoreService.addDocument(repliesRef, replyData);
  }

  // User Operations
  static async getUserById(userId: string): Promise<any> {
    try {
      return await FirestoreService.getDocument('Users', userId);
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  }

  // Like/Unlike Reply Methods
  static async likeReply(forumId: string, replyId: string, userId: string): Promise<void> {
    try {
      // Import Firestore functions
      const { doc, getDoc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      const replyRef = doc(db, 'CommunityForums', forumId, 'Replies', replyId);
      
      // Get current reply data
      const replySnap = await getDoc(replyRef);
      if (!replySnap.exists()) {
        throw new Error('Reply not found');
      }

      const replyData = replySnap.data();
      const currentLikedBy = replyData.likedBy || [];
      const currentLikeCount = replyData.likeCount || 0;

      // Check if user already liked this reply
      if (currentLikedBy.includes(userId)) {
        console.log('User already liked this reply');
        return;
      }

      // Add user to likedBy array and increment like count
      const updatedLikedBy = [...currentLikedBy, userId];
      const updatedLikeCount = currentLikeCount + 1;

      await updateDoc(replyRef, {
        likedBy: updatedLikedBy,
        likeCount: updatedLikeCount,
        updatedAt: Date.now().toString()
      });

      console.log('✅ Reply liked successfully');
    } catch (error) {
      console.error('❌ Error liking reply:', error);
      throw error;
    }
  }

  static async unlikeReply(forumId: string, replyId: string, userId: string): Promise<void> {
    try {
      // Import Firestore functions
      const { doc, getDoc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      const replyRef = doc(db, 'CommunityForums', forumId, 'Replies', replyId);
      
      // Get current reply data
      const replySnap = await getDoc(replyRef);
      if (!replySnap.exists()) {
        throw new Error('Reply not found');
      }

      const replyData = replySnap.data();
      const currentLikedBy = replyData.likedBy || [];
      const currentLikeCount = replyData.likeCount || 0;

      // Check if user has liked this reply
      if (!currentLikedBy.includes(userId)) {
        console.log('User has not liked this reply');
        return;
      }

      // Remove user from likedBy array and decrement like count
      const updatedLikedBy = currentLikedBy.filter(id => id !== userId);
      const updatedLikeCount = Math.max(0, currentLikeCount - 1);

      await updateDoc(replyRef, {
        likedBy: updatedLikedBy,
        likeCount: updatedLikeCount,
        updatedAt: Date.now().toString()
      });

      console.log('✅ Reply unliked successfully');
    } catch (error) {
      console.error('❌ Error unliking reply:', error);
      throw error;
    }
  }

  static async createForum(data: CreateForumData, userId: string): Promise<string> {
    const forumData: Omit<CommunityForum, 'id'> = {
      title: data.title,
      description: data.description,
      subject: data.subject,
      categoryId: data.categoryId, // Store only the category ID as string
      isActive: true,
      threadCount: 0,
      moderators: [userId], // Creator becomes first moderator
      tags: data.tags,
      isPinned: false,
      isLocked: false,
      rules: data.rules,
      icon: data.icon,
      color: data.color,
      createdAt: Date.now().toString(),
      updatedAt: Date.now().toString(),
      createdBy: userId,
      updatedBy: userId,
      isDeleted: false,
      isArchived: false
    };

    return FirestoreService.addDocument(FORUMS_REF, forumData);
  }

  static async createThread(data: CreateThreadData, userId: string): Promise<string> {
    const threadData: Omit<ForumThread, 'id'> = {
      forumId: data.forumId,
      title: data.title,
      content: data.content,
      authorId: userId,
      authorName: '', // Will be populated from user data
      authorPhotoURL: '',
      authorTitle: '',
      isPinned: false,
      isLocked: false,
      viewCount: 0,
      likeCount: 0,
      replyCount: 0,
      tags: data.tags,
      attachments: [], // Will be populated after file upload
      threadType: data.threadType,
      createdAt: Date.now().toString(),
      updatedAt: Date.now().toString(),
      createdBy: userId,
      updatedBy: userId,
      isDeleted: false,
      isArchived: false
    };

    return FirestoreService.addDocument(FORUM_THREADS_REF, threadData);
  }

  static async addThreadReply(threadId: string, content: string, userId: string, parentReplyId?: string): Promise<string> {
    // Get user data from Firestore
    let userData = { name: '', email: '', photoURL: '', title: '' };
    try {
      const userDoc = await FirestoreService.getDocument('Users', userId);
      if (userDoc) {
        // Try different name fields that might exist
        const userName = userDoc.name || 
                        userDoc.displayName || 
                        userDoc.fullName || 
                        userDoc.firstName + ' ' + userDoc.lastName || 
                        userDoc.email?.split('@')[0] || 
                        'Anonymous';
        
        userData = {
          name: userName,
          email: userDoc.email || '',
          photoURL: userDoc.photoURL || userDoc.avatar || '',
          title: userDoc.title || userDoc.jobTitle || userDoc.position || ''
        };
      }
    } catch (error) {
      console.warn('Could not fetch user data for thread reply, using defaults:', error);
    }

    // Create complete reply data with all fields and default values
    // This ensures consistent structure in Firestore with no undefined fields
    const replyData: any = {
      threadId,
      content,
      authorId: userId,
      authorName: userData.name,
      authorPhotoURL: userData.photoURL || '', // Always include with default
      authorTitle: userData.title || '', // Always include with default
      parentReplyId: parentReplyId || '', // Always include with default
      likeCount: 0,
      likedBy: [], // Always initialize empty array for likes
      isEdited: false,
      createdAt: Date.now().toString(),
      updatedAt: Date.now().toString(),
      createdBy: userId,
      updatedBy: userId,
      isDeleted: false,
      isArchived: false
    };

    return FirestoreService.addDocument(THREAD_REPLIES_REF, replyData);
  }

  // Event operations have been moved to CommunityEventsService

  // Connection Operations
  static async getConnections(userId: string): Promise<CommunityConnection[]> {
    return FirestoreService.getDocuments<CommunityConnection>(CONNECTIONS_REF, [
      FirestoreService.where('requesterId', '==', userId),
      FirestoreService.where('isDeleted', '==', false)
    ]);
  }

  static async getConnectionRequests(userId: string): Promise<CommunityConnection[]> {
    return FirestoreService.getDocuments<CommunityConnection>(CONNECTIONS_REF, [
      FirestoreService.where('receiverId', '==', userId),
      FirestoreService.where('status', '==', 'pending'),
      FirestoreService.where('isDeleted', '==', false)
    ]);
  }

  static async sendConnectionRequest(receiverId: string, requesterId: string, message?: string): Promise<string> {
    const connectionData: Omit<CommunityConnection, 'id'> = {
      requesterId,
      requesterName: '', // Will be populated from user data
      requesterPhotoURL: '',
      requesterTitle: '',
      requesterOffice: '',
      receiverId,
      receiverName: '', // Will be populated from user data
      receiverPhotoURL: '',
      receiverTitle: '',
      receiverOffice: '',
      status: 'pending',
      message,
      mutualConnections: 0,
      createdAt: Date.now().toString(),
      updatedAt: Date.now().toString(),
      createdBy: requesterId,
      updatedBy: requesterId,
      isDeleted: false,
      isArchived: false
    };

    return FirestoreService.addDocument(CONNECTIONS_REF, connectionData);
  }

  static async updateConnectionStatus(connectionId: string, status: ConnectionStatus): Promise<void> {
    await FirestoreService.updateDocument(CONNECTIONS_REF, connectionId, {
      status,
      updatedAt: Date.now().toString(),
      ...(status === 'accepted' && { connectedAt: Date.now().toString() })
    });
  }

  // Profile Operations
  static async getLawyerProfile(userId: string): Promise<LawyerProfile | null> {
    return FirestoreService.getDocument<LawyerProfile>(LAWYER_PROFILES_REF, userId);
  }

  static async updateLawyerProfile(userId: string, data: UpdateLawyerProfileData): Promise<void> {
    await FirestoreService.updateDocument(LAWYER_PROFILES_REF, userId, {
      ...data,
      updatedAt: Date.now().toString()
    });
  }

  // Category and Type Operations
  static async getForumCategories(): Promise<ForumCategory[]> {
    return FirestoreService.getDocuments<ForumCategory>(FORUM_CATEGORIES_REF, [
      FirestoreService.where('isActive', '==', true),
      FirestoreService.orderBy('order', 'asc')
    ]);
  }


  // Statistics
  static async getCommunityStats(): Promise<CommunityStats> {
    // This would typically involve multiple queries and aggregations
    // For now, returning a basic structure
    return {
      totalForums: 0,
      totalThreads: 0,
      totalReplies: 0,
      totalEvents: 0,
      totalConnections: 0,
      activeUsers: 0,
      popularForums: [],
      upcomingEvents: [],
      recentThreads: []
    };
  }

  // Search Operations
  static async searchThreads(query: string, limit = 20): Promise<ForumThread[]> {
    // Note: This is a simplified search. In production, you'd want to use
    // a proper search service like Algolia or Elasticsearch
    return FirestoreService.getDocuments<ForumThread>(FORUM_THREADS_REF, [
      FirestoreService.where('isDeleted', '==', false),
      FirestoreService.limit(limit)
    ]);
  }

  static async searchLawyers(query: string, limit = 20): Promise<LawyerProfile[]> {
    return FirestoreService.getDocuments<LawyerProfile>(LAWYER_PROFILES_REF, [
      FirestoreService.where('isDeleted', '==', false),
      FirestoreService.where('isPublic', '==', true),
      FirestoreService.limit(limit)
    ]);
  }
}
